import { Component, ChangeDetectionStrategy, OnInit, inject, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ChipModule } from 'primeng/chip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageModule } from 'primeng/message';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import {
  ContactTeachabilityService,
  ContactTeachability,
} from '../../core/services/contact-teachability.service';
import { AuthService } from '../../services/auth.service';
import { ContactService } from '../../core/services/contact.service';
import { PortalUiService } from '../../services/portal-ui.service';
import { TranslatePipe } from '../../core/features/translations';

@Component({
  selector: 'app-teachability',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    TableModule,
    ChipModule,
    ProgressSpinnerModule,
    MessageModule,
    TagModule,
    ButtonModule,
    InputTextModule,
    Select,
    MultiSelect,
    TranslatePipe,
  ],
  template: `
    <div class="p-6 flex flex-col gap-6 h-full overflow-hidden bg-gray-50/30 font-sans">
      <!-- Manager Information Cards (Bullet/Radio Filter) -->
      @if (managers().length > 0) {
        <div class="flex flex-wrap gap-4 p-4 bg-white rounded-2xl shadow-sm border border-gray-100 animate-slide-down transition-all">
          @for (m of managers(); track (m.fk_school_id || m.school)) {
            <button
              type="button"
              (click)="toggleSchoolFilter(m.school)"
              class="flex items-center gap-3.5 px-4 py-3 rounded-xl border text-left transition-all duration-200 cursor-pointer group"
              [class.bg-blue-50]="selectedSchoolShortname() === m.school"
              [class.border-blue-500]="selectedSchoolShortname() === m.school"
              [class.ring-2]="selectedSchoolShortname() === m.school"
              [class.ring-blue-500/20]="selectedSchoolShortname() === m.school"
              [class.bg-white]="selectedSchoolShortname() !== m.school"
              [class.border-gray-200]="selectedSchoolShortname() !== m.school"
              [class.hover:border-gray-300]="selectedSchoolShortname() !== m.school"
              [class.hover:bg-gray-50/60]="selectedSchoolShortname() !== m.school"
            >
              <!-- Bullet Radio Indicator -->
              <div 
                class="w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all shrink-0"
                [class.border-blue-600]="selectedSchoolShortname() === m.school"
                [class.border-gray-300]="selectedSchoolShortname() !== m.school"
                [class.group-hover:border-blue-400]="selectedSchoolShortname() !== m.school"
              >
                @if (selectedSchoolShortname() === m.school) {
                  <div class="w-2 h-2 rounded-full bg-blue-600 animate-scale-in"></div>
                }
              </div>

              <!-- Details -->
              <div class="flex flex-col">
                <div class="flex items-center gap-2">
                  <span class="text-xs font-bold uppercase tracking-wider text-gray-900 group-hover:text-blue-600 transition-colors">
                    {{ m.school_name || m.school }}
                  </span>
                  @if (m.school_name && m.school_name !== m.school) {
                    <span class="text-[10px] text-gray-400 font-medium">({{ m.school }})</span>
                  }
                </div>
                <div class="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                  <span>{{ 'portal.teachability.manager' | translate }}: <strong class="text-gray-700 font-semibold">{{ m.manager }}</strong></span>
                  <span class="text-gray-300">•</span>
                  <div class="flex items-center gap-1 text-[11px]">
                    <span class="text-gray-400">{{ 'portal.teachability.billing_status' | translate }}:</span>
                    @if (getSchoolBillingStatus(m.school) === 'all') {
                      <span class="material-symbols-outlined text-green-500 text-sm select-none" title="All billed">check_circle</span>
                    } @else if (getSchoolBillingStatus(m.school) === 'mixed') {
                      <span class="material-symbols-outlined text-amber-500 text-sm select-none animate-pulse" title="Partially billed">warning</span>
                    } @else {
                      <span class="material-symbols-outlined text-red-500 text-sm select-none" title="Not billed">cancel</span>
                    }
                  </div>
                </div>
              </div>
            </button>
          }

          <!-- Show All Schools Toggle Button when multiple schools exist -->
          @if (managers().length > 1) {
            <button
              type="button"
              (click)="selectedSchoolShortname.set(null)"
              class="flex items-center gap-2 px-3.5 py-3 rounded-xl border text-xs font-semibold uppercase tracking-wider transition-all duration-200 cursor-pointer self-center"
              [class.bg-blue-600]="selectedSchoolShortname() === null"
              [class.text-white]="selectedSchoolShortname() === null"
              [class.border-blue-600]="selectedSchoolShortname() === null"
              [class.bg-gray-100]="selectedSchoolShortname() !== null"
              [class.text-gray-600]="selectedSchoolShortname() !== null"
              [class.border-gray-200]="selectedSchoolShortname() !== null"
              [class.hover:bg-gray-200]="selectedSchoolShortname() !== null"
            >
              <span class="material-symbols-outlined text-base">apps</span>
              <span>Alle Schulen</span>
            </button>
          }
        </div>
      }

      <!-- Error Message -->
      @if (error()) {
        <div class="p-5 bg-red-50 border-l-4 border-red-500 rounded-2xl flex items-center gap-4 animate-slide-down shadow-xl shadow-red-500/10">
          <div class="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
            <span class="material-symbols-outlined text-red-500 text-xl">error</span>
          </div>
          <div class="text-red-900 text-lg tracking-tight">{{ error() }}</div>
        </div>
      }

      <!-- PrimeNG Table Section -->
      @if (!loading() && !error() && rawTeachabilities().length > 0) {
        <div class="flex-1 min-h-0 bg-white rounded-3xl shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden flex flex-col transition-all hover:shadow-gray-300/50">
            <p-table #dt 
              [value]="filteredTeachabilities()" 
              [paginator]="true" 
              [rows]="25"
              [rowsPerPageOptions]="[10, 25, 50, 100]" 
              [showCurrentPageReport]="true"
              currentPageReportTemplate="{first} bis {last} von {totalRecords} Einträgen" 
              [scrollable]="true"
              scrollHeight="flex" 
              [tableStyle]="{ 'min-width': '100%' }"
              class="flex-1 flex flex-col min-h-0"
              [pt]="tablePT"
            >
              <!-- Caption with Search and Filters -->
              <ng-template pTemplate="caption">
                <!-- Caption Header: Side-by-side on desktop, stacked on mobile -->
                <div class="flex flex-col xl:flex-row xl:items-center justify-between gap-4 p-6 bg-gray-50/50 border-b border-gray-100">
                  
                  <!-- Filters Group: Left-aligned -->
                  <div class="flex flex-wrap items-center gap-2 flex-1">
                    <p-select 
                      [options]="schoolShortnameOptions()" 
                      [ngModel]="selectedSchoolShortname()"
                      (ngModelChange)="selectedSchoolShortname.set($event)" 
                      placeholder="Schule" 
                      [showClear]="true"
                      [filter]="true" 
                      optionLabel="label"
                      optionValue="value"
                      [appendTo]="'self'"
                      filterPlaceholder="Schule suchen" 
                      class="min-w-[140px]"
                      [pt]="selectPT"
                    ></p-select>

                    <p-select 
                      [options]="teachabilityCodeOptions()" 
                      [ngModel]="selectedTeachabilityCode()"
                      (ngModelChange)="selectedTeachabilityCode.set($event)" 
                      placeholder="HR Code" 
                      [showClear]="true"
                      [filter]="true" 
                      optionLabel="label"
                      optionValue="value"
                      [appendTo]="'self'"
                      filterPlaceholder="HR Code suchen" 
                      class="min-w-[140px]"
                      [pt]="monoSelectPT"
                    ></p-select>

                    <p-select 
                      [options]="moduleCodeOptions()" 
                      [ngModel]="selectedModuleCode()"
                      (ngModelChange)="selectedModuleCode.set($event)" 
                      placeholder="Modul Code" 
                      [showClear]="true" 
                      [filter]="true"
                      optionLabel="label"
                      optionValue="value"
                      [appendTo]="'self'"
                      filterPlaceholder="Modul Code suchen" 
                      class="min-w-[160px]"
                      [pt]="monoSelectPT"
                    ></p-select>

                    <p-multiSelect 
                      [options]="quarterOptions" 
                      [ngModel]="selectedQuarters()"
                      (ngModelChange)="selectedQuarters.set($event)" 
                      placeholder="Quartal" 
                      optionLabel="label"
                      optionValue="value"
                      [appendTo]="'self'"
                      class="min-w-[140px]"
                      [display]="'chip'"
                      [pt]="multiSelectPT"
                    ></p-multiSelect>

                    @if (hasActiveFilters()) {
                      <button 
                        type="button"
                        (click)="clearAllFilters()"
                        class="flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-widest text-red-500 bg-red-50 rounded-xl hover:bg-red-500 hover:text-white transition-all active:scale-95 shadow-lg shadow-red-500/5 ring-1 ring-red-100"
                      >
                        <span class="material-symbols-outlined text-lg">filter_list_off</span>
                        <span>Reset</span>
                      </button>
                    }
                  </div>

                  <!-- Search Box: Right-aligned -->
                  <div class="relative w-full md:w-80 group">
                    <span class="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg group-focus-within:text-blue-500 transition-colors">search</span>
                    <input 
                      pInputText 
                      type="text" 
                      [ngModel]="globalFilterValue()"
                      (ngModelChange)="globalFilterValue.set($event)" 
                      placeholder="Durchsuchen..." 
                      class="w-full pl-11 pr-4 h-11 bg-white border-2 border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm transition-all shadow-sm hover:border-gray-200"
                    />
                  </div>
                </div>
              </ng-template>

              <!-- Table Header -->
              <ng-template pTemplate="header">
                <tr class="border-b-2 border-gray-100 bg-gray-50/50">
                    <th pSortableColumn="school_shortname" class="px-6 py-2 text-[0.65rem] text-gray-400 uppercase tracking-widest transition-colors hover:text-blue-500 w-[120px]" [pt]="headerCellPT">
                      <div class="flex items-center gap-2">Schule <p-sortIcon field="school_shortname" class="text-[8px]"></p-sortIcon></div>
                    </th>
                    <th pSortableColumn="teachability_code" class="px-6 py-2 text-[0.65rem] text-gray-400 uppercase tracking-widest transition-colors hover:text-blue-500 w-[140px]" [pt]="headerCellPT">
                      <div class="flex items-center gap-2">HR Code <p-sortIcon field="teachability_code" class="text-[8px]"></p-sortIcon></div>
                    </th>
                    <th pSortableColumn="lecture_wage" class="px-6 py-2 text-[0.65rem] text-gray-400 uppercase tracking-widest text-center transition-colors hover:text-blue-500 w-[120px]" [pt]="headerCellPT">
                      <div class="flex items-center justify-center gap-2">Ansatz <p-sortIcon field="lecture_wage" class="text-[8px]"></p-sortIcon></div>
                    </th>
                    <th pSortableColumn="module_code" class="px-6 py-2 text-[0.65rem] text-gray-400 uppercase tracking-widest transition-colors hover:text-blue-500 w-[140px]" [pt]="headerCellPT">
                      <div class="flex items-center gap-2">Modul Code <p-sortIcon field="module_code" class="text-[8px]"></p-sortIcon></div>
                    </th>
                    <th pSortableColumn="module_name" class="px-6 py-2 text-[0.65rem] text-gray-400 uppercase tracking-widest transition-colors hover:text-blue-500 min-w-[200px]" [pt]="headerCellPT">
                      <div class="flex items-center gap-2">Modul Name <p-sortIcon field="module_name" class="text-[8px]"></p-sortIcon></div>
                    </th>
                    <th class="px-4 py-2 text-[0.65rem] text-gray-400 uppercase tracking-widest text-center w-[160px]">
                      <div class="flex items-center justify-center gap-1">
                        {{ 'portal.teachability.opt_out' | translate }}
                      </div>
                    </th>
                    <th pSortableColumn="planning_q1" class="px-2 py-2 text-[0.65rem] text-gray-400 uppercase tracking-widest text-center transition-colors hover:text-blue-500 w-[60px]" [pt]="headerCellPT">
                      <div class="flex items-center justify-center gap-1">Q1 <p-sortIcon field="planning_q1" class="text-[8px]"></p-sortIcon></div>
                    </th>
                    <th pSortableColumn="planning_q2" class="px-2 py-2 text-[0.65rem] text-gray-400 uppercase tracking-widest text-center transition-colors hover:text-blue-500 w-[60px]" [pt]="headerCellPT">
                      <div class="flex items-center justify-center gap-1">Q2 <p-sortIcon field="planning_q2" class="text-[8px]"></p-sortIcon></div>
                    </th>
                    <th pSortableColumn="planning_q3" class="px-2 py-2 text-[0.65rem] text-gray-400 uppercase tracking-widest text-center transition-colors hover:text-blue-500 w-[60px]" [pt]="headerCellPT">
                      <div class="flex items-center justify-center gap-1">Q3 <p-sortIcon field="planning_q3" class="text-[8px]"></p-sortIcon></div>
                    </th>
                    <th pSortableColumn="planning_q4" class="px-2 py-2 text-[0.65rem] text-gray-400 uppercase tracking-widest text-center transition-colors hover:text-blue-500 w-[60px]" [pt]="headerCellPT">
                      <div class="flex items-center justify-center gap-1">Q4 <p-sortIcon field="planning_q4" class="text-[8px]"></p-sortIcon></div>
                    </th>
                  </tr>
              </ng-template>

            <!-- Table Body -->
            <ng-template pTemplate="body" let-item>
              <tr [pSelectableRow]="item" class="hover:bg-blue-50/10 transition-all duration-300 group border-b border-gray-50/50">
                <td class="px-6 py-2">
                  @if (item.school_shortname) {
                    <span 
                      class="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-xl text-[10px] uppercase tracking-widest shadow-sm ring-1 ring-blue-100 group-hover:bg-blue-600 group-hover:text-white transition-all duration-500 group-hover:-rotate-2 inline-block cursor-help"
                      [title]="item.school_name || item.school_shortname"
                    >
                      {{ item.school_shortname }}
                    </span>
                  } @else {
                    <span class="text-gray-200 italic text-xs">-</span>
                  }
                </td>
                <td class="px-6 py-2">
                  @if (item.teachability_code) {
                    <span [ngClass]="getHRCodeClasses(item.teachability_code)"
                          class="px-2.5 py-1 rounded-xl text-[10px] uppercase tracking-widest shadow-sm ring-1 group-hover:text-white transition-all duration-500 group-hover:rotate-2 font-mono">
                      {{ item.teachability_code }}
                    </span>
                  } @else {
                    <span class="text-gray-200 italic text-xs">-</span>
                  }
                </td>
                <td class="px-6 py-2 text-center">
                  @if (item.lecture_wage !== null) {
                    <span class="text-sm text-gray-700 group-hover:text-blue-700 transition-colors whitespace-nowrap">
                      {{ item.lecture_wage | number: '1.2-2' }} <span class="text-[9px] text-gray-400 uppercase ml-0.5 group-hover:text-blue-400 transition-colors">CHF</span>
                    </span>
                  } @else {
                    <span class="text-gray-200 italic text-xs">-</span>
                  }
                </td>
                <td class="px-6 py-2">
                  @if (item.module_code) {
                    @if (item.pdf_url) {
                      <a [href]="item.pdf_url" target="_blank" class="text-[0.8rem] text-blue-600 font-black uppercase tracking-widest transition-all hover:text-blue-800 hover:underline flex items-center gap-1.5 group/link">
                        {{ item.module_code }}
                        <span class="material-symbols-outlined text-[10px] opacity-40 group-hover/link:opacity-100 transition-opacity">open_in_new</span>
                      </a>
                    } @else {
                      <span class="text-[0.8rem] text-gray-500 uppercase tracking-tighter transition-all group-hover:text-blue-500">
                        {{ item.module_code }}
                      </span>
                    }
                  } @else {
                    <span class="text-gray-200 italic text-xs">-</span>
                  }
                </td>
                <td class="px-6 py-2">
                  @if (item.module_name) {
                    <span class="text-sm text-gray-900 group-hover:text-blue-700 transition-all duration-300 leading-tight block" [title]="item.module_name">
                      {{ item.module_name }}
                    </span>
                  } @else {
                    <span class="text-gray-200 italic text-xs">-</span>
                  }
                </td>
                <td class="px-4 py-2 text-center" (click)="$event.stopPropagation()">
                  <input
                    type="checkbox"
                    [checked]="item.opt_out"
                    (change)="onOptOutChange(item, $event)"
                    class="w-4 h-4 cursor-pointer accent-blue-600 rounded"
                    [title]="'portal.teachability.opt_out' | translate"
                  />
                </td>
                <td class="px-2 py-2 text-center">
                  <ng-container *ngTemplateOutlet="statusCell; context: { $implicit: item.planning_q1 }"></ng-container>
                </td>
                <td class="px-2 py-2 text-center">
                  <ng-container *ngTemplateOutlet="statusCell; context: { $implicit: item.planning_q2 }"></ng-container>
                </td>
                <td class="px-2 py-2 text-center">
                  <ng-container *ngTemplateOutlet="statusCell; context: { $implicit: item.planning_q3 }"></ng-container>
                </td>
                <td class="px-2 py-2 text-center">
                  <ng-container *ngTemplateOutlet="statusCell; context: { $implicit: item.planning_q4 }"></ng-container>
                </td>
              </tr>
            </ng-template>

            <!-- Status Template -->
            <ng-template #statusCell let-status>
              <div class="flex justify-center">
                @if (status === true) {
                  <div class="w-7 h-7 bg-green-50 text-green-600 rounded-lg flex items-center justify-center shadow-sm ring-1 ring-green-100 transition-all group-hover:scale-110 group-hover:bg-green-600 group-hover:text-white">
                    <span class="material-symbols-outlined text-base font-bold">check</span>
                  </div>
                } @else if (status === false) {
                  <div class="w-7 h-7 bg-red-50 text-red-300 rounded-lg flex items-center justify-center ring-1 ring-red-100/30 group-hover:scale-110 group-hover:bg-red-500 group-hover:text-white transition-all">
                    <span class="material-symbols-outlined text-base font-bold">close</span>
                  </div>
                } @else {
                  <span class="text-gray-100 font-black text-xs">-</span>
                }
              </div>
            </ng-template>

            <!-- Empty Table -->
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="11" class="text-center p-32 bg-gray-50/20">
                  <div class="flex flex-col items-center gap-6 animate-pulse-slow">
                    <div class="w-24 h-24 bg-white rounded-3xl flex items-center justify-center shadow-2xl ring-1 ring-gray-100">
                      <span class="material-symbols-outlined text-gray-200 text-6xl">search_off</span>
                    </div>
                    <div>
                      <p class="text-gray-900 text-3xl font-black m-0 mb-3 tracking-tight">Keine Ergebnisse</p>
                      <p class="text-gray-400 font-black text-xs uppercase tracking-widest max-w-xs mx-auto leading-relaxed">Bitte überprüfen Sie Ihre Filtereinstellungen oder versuchen Sie es mit einem anderen Begriff.</p>
                    </div>
                    <button 
                      (click)="clearAllFilters()"
                      class="px-8 py-3 bg-white text-gray-700 border-2 border-gray-100 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-gray-50 active:scale-95 transition-all outline-none ring-offset-2 focus:ring-2 focus:ring-gray-200"
                    >
                      Alle Filter löschen
                    </button>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      }

      <!-- Loading State -->
      @if (loading()) {
        <div class="flex-1 flex flex-col items-center justify-center p-20 bg-white rounded-[2rem] border border-gray-100 shadow-2xl shadow-gray-200/50">
          <div class="relative w-24 h-24 mb-10">
            <div class="absolute inset-0 border-8 border-gray-50 rounded-full"></div>
            <div class="absolute inset-0 border-8 border-transparent border-t-blue-600 rounded-full animate-spin"></div>
            <div class="absolute inset-0 flex items-center justify-center text-blue-600">
              <span class="material-symbols-outlined text-3xl animate-pulse">sync</span>
            </div>
          </div>
          <p class="text-gray-400 text-2xl font-black tracking-tight animate-pulse uppercase">Daten werden geladen...</p>
        </div>
      }

      <!-- No Data Root Message -->
      @if (!loading() && !error() && rawTeachabilities().length === 0) {
        <div class="flex-1 flex flex-col items-center justify-center p-20 bg-white rounded-[2.5rem] border border-gray-100 shadow-2xl shadow-gray-200/50 text-center animate-fade-in group hover:shadow-blue-200/20 transition-all duration-700">
           <div class="relative w-40 h-40 mb-12">
              <div class="absolute inset-0 bg-blue-50 rounded-[3rem] animate-ping opacity-20"></div>
              <div class="relative w-40 h-40 bg-gradient-to-br from-blue-50 to-blue-100 rounded-[3rem] flex items-center justify-center shadow-2xl ring-4 ring-white group-hover:scale-110 group-hover:rotate-12 transition-all duration-700">
                <span class="material-symbols-outlined text-blue-600 text-7xl">badge</span>
              </div>
           </div>
          <h3 class="text-gray-900 text-4xl font-black m-0 mb-6 tracking-tighter">Keine Teachability-Daten</h3>
          <p class="text-gray-400 max-w-sm mx-auto text-xl font-bold leading-relaxed mb-10 uppercase tracking-tight opacity-60">Sie haben aktuell keine zugewiesenen Module in Ihrem Profil.</p>
          <button 
            (click)="loadData()"
            class="flex items-center justify-center gap-3 px-12 py-5 bg-blue-600 text-white rounded-[1.5rem] font-black text-xl shadow-[0_20px_40px_rgba(37,99,235,0.3)] hover:bg-blue-700 hover:shadow-[0_25px_50px_rgba(37,99,235,0.4)] active:scale-95 transition-all duration-300 focus:outline-none focus:ring-8 focus:ring-blue-100 group mx-auto"
          >
            <span class="material-symbols-outlined text-3xl transition-transform duration-700 group-hover:rotate-180">refresh</span>
            <span>Aktualisieren</span>
          </button>
        </div>
      }
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeachabilityComponent implements OnInit {
  private readonly teachabilityService = inject(ContactTeachabilityService);
  private readonly authService = inject(AuthService);
  private readonly contactService = inject(ContactService);
  private readonly uiService = inject(PortalUiService);

  // Service signals
  readonly loading = this.teachabilityService.loading;
  readonly error = this.teachabilityService.error;
  readonly rawTeachabilities = this.teachabilityService.teachabilities;
  readonly managers = this.teachabilityService.managers;

  // Pass-Through Styling Configurations
  readonly tablePT = {
    root: 'flex flex-col h-full bg-white',
    header: 'bg-gray-50/50 border-b border-gray-100',
    body: 'divide-y divide-gray-50',
    pcPaginator: {
      root: 'p-4 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between'
    }
  };

  readonly headerCellPT = { root: 'bg-gray-50/50' };

  readonly selectPT = {
    root: 'relative flex items-center h-11 bg-white border-2 border-gray-100 rounded-xl shadow-sm hover:border-gray-200 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all px-3 gap-2 cursor-pointer text-sm',
    input: 'flex-1 outline-none text-gray-700',
    overlay: 'absolute top-full left-0 w-max min-w-full mt-2 bg-gray-100 border border-gray-200 rounded-xl shadow-2xl z-[2000] overflow-hidden',
    list: 'p-1 max-h-[300px] overflow-y-auto overflow-x-hidden bg-gray-100',
    option: ({ context }: any) => ({
      class: 'px-3 py-2 rounded-lg cursor-pointer transition-colors flex items-center gap-2 whitespace-nowrap ' + (context.selected ? 'bg-blue-600 text-white' : 'hover:bg-blue-50 text-gray-700 font-medium')
    })
  };

  readonly monoSelectPT = {
    root: 'relative flex items-center h-11 bg-white border-2 border-gray-100 rounded-xl shadow-sm hover:border-gray-200 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all px-3 gap-2 cursor-pointer text-sm',
    input: 'flex-1 outline-none text-gray-700 font-mono',
    overlay: 'absolute top-full left-0 w-max min-w-full mt-2 bg-gray-100 border border-gray-200 rounded-xl shadow-2xl z-[2000] overflow-hidden',
    list: 'p-1 max-h-[300px] overflow-y-auto overflow-x-hidden bg-gray-100',
    option: ({ context }: any) => ({
      class: 'px-3 py-2 rounded-lg cursor-pointer transition-colors flex items-center gap-2 whitespace-nowrap ' + (context.selected ? 'bg-blue-600 text-white' : 'hover:bg-blue-50 text-gray-700 font-medium')
    })
  };

  readonly multiSelectPT = {
    root: 'relative flex items-center h-11 bg-white border-2 border-gray-100 rounded-xl shadow-sm hover:border-gray-200 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all px-3 gap-2 cursor-pointer text-sm',
    labelContainer: 'flex flex-nowrap overflow-hidden gap-1 p-1',
    token: 'bg-blue-600 text-white rounded-lg px-2 py-0.5 text-[10px] uppercase tracking-wider shadow-sm flex items-center gap-1 shrink-0',
    overlay: 'absolute top-full left-0 w-max min-w-full mt-2 bg-gray-100 border border-gray-200 rounded-xl shadow-2xl z-[2000] overflow-hidden',
    list: 'p-1 max-h-[300px] overflow-y-auto overflow-x-hidden bg-gray-100',
    option: ({ context }: any) => ({
      class: 'px-3 py-2 rounded-lg cursor-pointer transition-colors flex items-center flex-nowrap gap-2 whitespace-nowrap ' + (context.selected ? 'bg-blue-600 text-white' : 'hover:bg-blue-50 text-gray-700 font-medium')
    }),
    checkboxContainer: 'hidden',
    checkbox: 'hidden',
    checkboxIcon: 'hidden',
    pcCheckbox: {
      root: 'hidden'
    },
    headerCheckboxContainer: 'hidden',
    headerCheckbox: 'hidden',
    headerCheckboxIcon: 'hidden',
    optionIcon: 'hidden',
    itemCheckbox: 'hidden'
  };

  constructor() {
    // Sync total count to global header
    effect(() => {
      this.uiService.setPageCount(this.totalCount());
    });

    // Sync selected school to portal header logo banner
    effect(() => {
      const selected = this.selectedSchoolShortname();
      if (selected) {
        this.uiService.setActiveSchools(selected);
      } else {
        // If "Alle Schulen" selected, collect all distinct school shortnames from teachabilities or managers
        const allSchools = Array.from(
          new Set(
            this.managers()
              .map((m) => m.school)
              .filter((s): s is string => !!s && s.trim().length > 0)
          )
        );
        if (allSchools.length > 0) {
          this.uiService.setActiveSchools(allSchools);
        } else {
          this.uiService.setActiveSchools(null);
        }
      }
    });
  }

  // Filter signals
  readonly globalFilterValue = signal<string>('');
  readonly selectedSchoolShortname = signal<string | null>(null);
  readonly selectedModuleCode = signal<string | null>(null);
  readonly selectedTeachabilityCode = signal<string | null>(null);
  readonly selectedQuarters = signal<string[]>([]);

  // Quarter filter options
  readonly quarterOptions = [
    { label: 'Q1', value: 'Q1' },
    { label: 'Q2', value: 'Q2' },
    { label: 'Q3', value: 'Q3' },
    { label: 'Q4', value: 'Q4' },
  ];

  // Available filter options (computed from raw data)
  readonly schoolShortnameOptions = computed(() => {
    const schools = new Set<string>();
    this.rawTeachabilities().forEach(item => {
      if (item.school_shortname) schools.add(item.school_shortname);
    });
    return Array.from(schools).sort().map(s => ({ label: s, value: s }));
  });

  readonly moduleCodeOptions = computed(() => {
    const codes = new Set<string>();
    this.rawTeachabilities().forEach(item => {
      if (item.module_code) codes.add(item.module_code);
    });
    return Array.from(codes).sort().map(c => ({ label: c, value: c }));
  });

  readonly teachabilityCodeOptions = computed(() => {
    const codes = new Set<string>();
    this.rawTeachabilities().forEach(item => {
      if (item.teachability_code) codes.add(item.teachability_code);
    });
    return Array.from(codes).sort().map(c => ({ label: c, value: c }));
  });

  readonly moduleNameOptions = computed(() => {
    const names = new Set<string>();
    this.rawTeachabilities().forEach(item => {
      if (item.module_name) names.add(item.module_name);
    });
    return Array.from(names).sort().map(n => ({ label: n, value: n }));
  });

  /**
   * Filtered data stream applied to the table
   */
  readonly filteredTeachabilities = computed(() => {
    let filtered = this.rawTeachabilities();

    // Global text filter
    const globalFilter = this.globalFilterValue().toLowerCase();
    if (globalFilter) {
      filtered = filtered.filter(item =>
        item.school_shortname?.toLowerCase().includes(globalFilter) ||
        item.module_code?.toLowerCase().includes(globalFilter) ||
        item.module_name?.toLowerCase().includes(globalFilter) ||
        item.teachability_code?.toLowerCase().includes(globalFilter)
      );
    }

    // Dropdown filters
    const school = this.selectedSchoolShortname();
    if (school) {
      filtered = filtered.filter(item => item.school_shortname === school);
    }

    const code = this.selectedModuleCode();
    if (code) {
      filtered = filtered.filter(item => item.module_code === code);
    }

    const tCode = this.selectedTeachabilityCode();
    if (tCode) {
      filtered = filtered.filter(item => item.teachability_code === tCode);
    }

    // Quarter Filters (AND logic: must match all selected quarters)
    const quarters = this.selectedQuarters();
    if (quarters.length > 0) {
      filtered = filtered.filter(item => {
        let matches = true;
        if (quarters.includes('Q1') && item.planning_q1 !== true) matches = false;
        if (quarters.includes('Q2') && item.planning_q2 !== true) matches = false;
        if (quarters.includes('Q3') && item.planning_q3 !== true) matches = false;
        if (quarters.includes('Q4') && item.planning_q4 !== true) matches = false;
        return matches;
      });
    }

    return filtered;
  });

  // Stats computed based on filtered data
  readonly filteredStats = computed(() => {
    const filtered = this.filteredTeachabilities();
    const total = filtered.length;
    // "Published" for teachability means at least one quarter is assigned (true)
    const published = filtered.filter(item =>
      item.planning_q1 === true ||
      item.planning_q2 === true ||
      item.planning_q3 === true ||
      item.planning_q4 === true
    ).length;

    return { total, published };
  });

  // Complementary total count for paginator etc
  readonly totalCount = computed(() => this.filteredStats().total);

  ngOnInit(): void {
    this.loadData();
  }

  /**
   * Load teachability data for the logged-in user
   */
  public loadData(): void {
    // Get user info from auth service
    const userInfo = this.authService.getUserInfo();
    if (!userInfo) {
      this.teachabilityService.error.set('Benutzerinformationen nicht gefunden.');
      console.error('❌ No user info found');
      return;
    }

    // Extract email from user info (sub or email field)
    const userEmail = userInfo.sub || userInfo.email;
    if (!userEmail) {
      this.teachabilityService.error.set('E-Mail-Adresse nicht gefunden.');
      console.error('❌ No email found in user info');
      return;
    }

    // Load contact by email to get contact_id
    this.contactService.getContactByEmail(userEmail).subscribe({
      next: (contact) => {
        if (!contact || !contact.contact_id) {
          this.teachabilityService.error.set('Kontakt nicht gefunden.');
          console.error('❌ No contact found for email:', userEmail);
          return;
        }

        const contactId = contact.contact_id;

        // Fetch managers
        this.teachabilityService.getTeacherManagers(contactId).subscribe({
          next: (mgrs) => {
            if (mgrs && mgrs.length > 0 && !this.selectedSchoolShortname()) {
              // Default selection: pick the manager/school with the lowest fk_school_id
              const sorted = [...mgrs].sort((a, b) => {
                const idA = a.fk_school_id ?? 999999;
                const idB = b.fk_school_id ?? 999999;
                return idA - idB;
              });
              this.selectedSchoolShortname.set(sorted[0].school);
            }
          },
        });

        this.teachabilityService.getTeachabilities(contactId).subscribe({
          next: () => {
            // After teachabilities are loaded, fetch bulk PDF URLs
            this.teachabilityService.getModulplans(contactId).subscribe({
              next: (pdfLinks) => {
                if (!pdfLinks || pdfLinks.length === 0) return;
                
                // Merge PDF URLs into the current teachabilities signal array
                const currentData = [...this.teachabilityService.teachabilities()];
                const updatedData = currentData.map(item => {
                  const link = pdfLinks.find(l => l.module_code === item.module_code && l.school_id === item.fk_school_id);
                  return link ? { ...item, pdf_url: link.pdf_url } : item;
                });
                
                this.teachabilityService.teachabilities.set(updatedData);
              }
            });
          },
          error: (err) => {
            console.error('❌ Error loading teachabilities:', err);
          },
        });
      },
      error: (err) => {
        this.teachabilityService.error.set('Fehler beim Laden des Kontakts.');
        console.error('❌ Failed to load contact:', err);
      },
    });
  }

  /**
   * Toggle teacher opt_out status for teachability
   */
  onOptOutChange(item: ContactTeachability, event: Event): void {
    const input = event.target as HTMLInputElement;
    const newValue = input.checked;

    this.teachabilityService.updateOptOut(item.contact_teachability_id, newValue).subscribe({
      next: () => {
        item.opt_out = newValue;
      },
      error: (err) => {
        console.error('❌ Failed to update opt_out status:', err);
        // Revert checkbox state on error
        input.checked = !newValue;
      },
    });
  }

  /**
   * Toggle school filter from manager card click
   */
  toggleSchoolFilter(school: string): void {
    if (this.selectedSchoolShortname() === school) {
      // Toggle off to show all
      this.selectedSchoolShortname.set(null);
    } else {
      this.selectedSchoolShortname.set(school);
    }
  }

  /**
   * Reset all search and dropdown filters
   */
  clearAllFilters(): void {
    this.globalFilterValue.set('');
    this.selectedSchoolShortname.set(null);
    this.selectedModuleCode.set(null);
    this.selectedTeachabilityCode.set(null);
    this.selectedQuarters.set([]);
  }

  /**
   * Check if any dropdown filter is active
   */
  hasActiveFilters(): boolean {
    return !!(
      this.selectedSchoolShortname() ||
      this.selectedModuleCode() ||
      this.selectedTeachabilityCode() ||
      this.selectedQuarters().length > 0
    );
  }

  /**
   * Get dynamic color classes for HR Code badges based on string hash
   */
  getHRCodeClasses(code: string): string {
    if (!code) return '';

    const palettes = [
      'bg-blue-50 text-blue-700 ring-blue-100 group-hover:bg-blue-600',
      'bg-purple-50 text-purple-700 ring-purple-100 group-hover:bg-purple-600',
      'bg-emerald-50 text-emerald-700 ring-emerald-100 group-hover:bg-emerald-600',
      'bg-orange-50 text-orange-700 ring-orange-100 group-hover:bg-orange-600',
      'bg-pink-50 text-pink-700 ring-pink-100 group-hover:bg-pink-600',
      'bg-cyan-50 text-cyan-700 ring-cyan-100 group-hover:bg-cyan-600',
      'bg-indigo-50 text-indigo-700 ring-indigo-100 group-hover:bg-indigo-600',
      'bg-amber-50 text-amber-700 ring-amber-100 group-hover:bg-amber-600',
      'bg-rose-50 text-rose-700 ring-rose-100 group-hover:bg-rose-600',
      'bg-teal-50 text-teal-700 ring-teal-100 group-hover:bg-teal-600',
      'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-100 group-hover:bg-fuchsia-600',
      'bg-sky-50 text-sky-700 ring-sky-100 group-hover:bg-sky-600',
      'bg-violet-50 text-violet-700 ring-violet-100 group-hover:bg-violet-600'
    ];

    let hash = 0;
    for (let i = 0; i < code.length; i++) {
      hash = code.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % palettes.length;
    return palettes[index];
  }

  /**
   * Get aggregate billing status for a school
   */
  getSchoolBillingStatus(school: string): 'all' | 'none' | 'mixed' {
    const lines = this.rawTeachabilities().filter(t => t.school_shortname === school);
    if (lines.length === 0) return 'none';

    const billedCount = lines.filter(t => t.billed_party === true).length;

    if (billedCount === lines.length) return 'all';
    if (billedCount === 0) return 'none';
    return 'mixed';
  }
}
